import {Calendar, Mail, Phone, ShieldBan} from "lucide-react";

import {useStoredMe} from "@/src/modules/core/store/me";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";
import useHousehold from "@/src/modules/households/hooks/use-household";
import {
  getHouseholdAmenityAccessResidentMessage,
  getRestrictedUntilLabel,
  isHouseholdAmenityRestricted,
} from "@/src/modules/households/lib/household-amenity-restriction";
import {
  formatPropertyManagerDisplayName,
  pickPrimaryPropertyManager,
} from "@/src/modules/properties/lib/property-manager-contact";
import {formatUsPhoneDisplay, normalizeUsPhoneDigits} from "@/src/modules/properties/lib/us-phone";
import {cn} from "@/lib/utils";

import {RESIDENT_HOME_BLOCK_SHELL_CLASS} from "../lib/resident-home-block-layout";
import {
  hasAnyHouseholdAmenityRestriction,
  RESIDENT_GENERIC_AMENITY_RESTRICTION_MESSAGE,
} from "../lib/resident-amenity-restriction-copy";

function InlineSeparator() {
  return <span className="text-destructive/35 hidden sm:inline" aria-hidden>·</span>;
}

type ResidentHomeAmenityRestrictionBarProps = {
  className?: string;
};

export function ResidentHomeAmenityRestrictionBar({className}: ResidentHomeAmenityRestrictionBarProps) {
  const me = useStoredMe();
  const userProperty = useStoredUserProperty();
  const householdId = me?.house_hold_id ?? null;
  const {household} = useHousehold(householdId);

  const isRestricted =
    household &&
    (isHouseholdAmenityRestricted(household) || hasAnyHouseholdAmenityRestriction(household));

  if (!isRestricted) {
    return null;
  }

  const residentMessage =
    getHouseholdAmenityAccessResidentMessage(household) ||
    RESIDENT_GENERIC_AMENITY_RESTRICTION_MESSAGE;
  const manager = pickPrimaryPropertyManager(userProperty?.property_managers);
  const managerName = manager ? formatPropertyManagerDisplayName(manager) : null;
  const managerEmail = manager?.email?.trim() || null;
  const managerPhone = manager?.telephone?.trim() || null;
  const managerTelHref = managerPhone ? `tel:${normalizeUsPhoneDigits(managerPhone)}` : null;
  const untilLabel = household.restricted_until?.trim()
    ? getRestrictedUntilLabel(household.restricted_until)
    : null;

  return (
    <div
      role="alert"
      className={cn(
        RESIDENT_HOME_BLOCK_SHELL_CLASS,
        "border-destructive/40 bg-destructive/5 text-destructive flex flex-wrap items-center gap-x-2 gap-y-1 py-2 text-xs sm:gap-x-3 sm:text-sm",
        className,
      )}
    >
      <span className="inline-flex shrink-0 items-center gap-1.5 font-semibold">
        <ShieldBan className="size-3.5 shrink-0" aria-hidden />
        Amenity access restricted
      </span>

      <InlineSeparator />

      <span className="text-destructive/90 min-w-0 text-pretty leading-snug">{residentMessage}</span>

      {untilLabel ? (
        <>
          <InlineSeparator />
          <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap">
            <Calendar className="size-3 shrink-0" aria-hidden />
            Until {untilLabel}
          </span>
        </>
      ) : null}

      {managerName || managerEmail || managerPhone ? (
        <>
          <InlineSeparator />
          <span className="inline-flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
            <span className="font-medium">Property Manager:</span>
            {managerName ? <span>{managerName}</span> : null}
            {managerEmail ? (
              <a href={`mailto:${managerEmail}`} className="inline-flex items-center gap-1 hover:underline">
                <Mail className="size-3 shrink-0" aria-hidden />
                {managerEmail}
              </a>
            ) : null}
            {managerPhone && managerTelHref ? (
              <a href={managerTelHref} className="inline-flex items-center gap-1 hover:underline">
                <Phone className="size-3 shrink-0" aria-hidden />
                {formatUsPhoneDisplay(managerPhone)}
              </a>
            ) : null}
          </span>
        </>
      ) : null}
    </div>
  );
}
